// The macro action builder's logic, with no React and no vocabulary in it.
//
// THIS MODULE HOLDS NO ACTION NAMES. Not one of `set_status`, `set_priority`,
// `assign`, `add_tag`, `low`, `on_hold` or their siblings appears anywhere in
// `web/src` — every list arrives as the `vocabulary` argument, served from the
// macros descriptor and sourced from the very arrays src/services/admin/macros.js
// validates against. That is the whole reason Task 11 refused to build this: a
// client copy of the vocabulary would, the first time the two disagreed, "quietly
// rewrite any action it did not recognise into one it did". Serving the vocabulary
// answers that objection rather than routing around it, and having no client
// literal to fall back on is what makes it safe — a missing vocabulary can only
// produce the JSON editor, never a wrong dropdown.
//
// NOTHING IS EVER DROPPED. `src/routing/actions.js` skips an action it does not
// know and New.jsx does the same; this module does neither. An entry whose verb is
// not in the served list becomes a LOCKED row that shows its own JSON and is
// re-emitted byte-identical. Removal is possible but always explicit.

/**
 * The server's own `maxItems` on the `actions` field, mirrored so the `+` can stop
 * at the cap instead of letting an admin build 21 rows and discover it as a 422.
 */
export const MAX_ACTION_ROWS = 20;

/**
 * The served vocabulary, or null when the server did not describe one.
 *
 * Null is the safe answer and the caller's contract is to fall back to the JSON
 * textarea — an older API behind a separately deployed `web/dist` is a real
 * possibility here. Anything malformed is treated as absent for the same reason.
 *
 * @param {object} field  the descriptor's `actions` field spec.
 */
export function actionVocabulary(field) {
  const vocabulary = field?.vocabulary;
  if (!vocabulary || typeof vocabulary !== 'object') return null;
  const types = vocabulary.types;
  if (!Array.isArray(types) || types.length === 0) return null;
  if (!types.every((t) => typeof t === 'string' && t.length > 0)) return null;
  return vocabulary;
}

/**
 * A verb or an enum value as a human reads it, derived MECHANICALLY from the string
 * itself. A label map would be a second copy of the vocabulary, and a verb the
 * server adds tomorrow would render blank; `set_status` to "Set status" needs no
 * maintenance and cannot drift.
 */
export function humanize(token) {
  const text = String(token ?? '').replace(/_/g, ' ').trim();
  if (text === '') return '';
  return text[0].toUpperCase() + text.slice(1);
}

/**
 * What the validator would read off a stored entry — the same two reads
 * src/services/admin/macros.js makes, in the same order, including `Object.hasOwn`
 * rather than a bare property read (`{ type: 'toString' }` picks a FUNCTION off
 * Object.prototype otherwise).
 */
function derive(entry) {
  const type = Object.hasOwn(entry, 'type') ? entry.type : undefined;
  const raw = Object.hasOwn(entry, 'value') ? entry.value : undefined;
  return { type, raw };
}

function isPlainObject(entry) {
  return typeof entry === 'object' && entry !== null && !Array.isArray(entry);
}

function asText(raw) {
  return raw === undefined || raw === null ? '' : String(raw);
}

/**
 * Stored JSON to builder rows. Every entry produces exactly one row; the array
 * length is preserved without exception.
 *
 *   { id, kind: 'known',   type, value, raw }   editable
 *   { id, kind: 'unknown', raw }                locked, shows its own JSON
 *
 * `raw` is kept on a KNOWN row too, and that is what makes the round trip
 * byte-identical rather than merely equivalent — see `rowsToActions`.
 *
 * A value that is neither string, number, null nor absent is locked rather than
 * coerced: the validator rejects it (macros.js:64), and `String({})` is exactly the
 * "[object Object]" that shipped on this screen once already.
 */
export function actionsToRows(actions, vocabulary) {
  if (!Array.isArray(actions)) return [];
  const types = vocabulary?.types ?? [];

  return actions.map((entry, i) => {
    const id = `row-${i}`;
    if (!isPlainObject(entry)) return { id, kind: 'unknown', raw: entry };

    const { type, raw } = derive(entry);
    if (!types.includes(type)) return { id, kind: 'unknown', raw: entry };
    if (raw !== undefined && raw !== null && typeof raw !== 'string' && typeof raw !== 'number') {
      return { id, kind: 'unknown', raw: entry };
    }

    return { id, kind: 'known', type, value: asText(raw), raw: entry };
  });
}

/** Whether a known row still says exactly what it was loaded saying. */
function pristine(row) {
  if (!isPlainObject(row.raw)) return false;
  const { type, raw } = derive(row.raw);
  return type === row.type && asText(raw) === row.value;
}

/**
 * Builder rows back to stored JSON.
 *
 * THE PHANTOM-DIFF RULE. `changedValues` compares arrays with `JSON.stringify`, so
 * a builder that re-emits `{value, type}`, or `"7"` where the row held `7`, makes
 * every untouched macro look edited and fills every `config_events` before/after
 * diff with changes nobody made. An UNTOUCHED row is therefore re-emitted as its
 * original object, byte for byte, key order and extra keys and all. Only a row
 * somebody actually changed is rewritten, and then in the server's own canonical
 * shape: `{ type, value }`, in that key order, value stringified — which is exactly
 * what macros.js:87 would store anyway.
 *
 * AN EDITED ROW KEEPS ITS VERB'S OTHER KEYS. Some verbs carry a second key the row
 * does not edit: `field` on set_custom_field (macros and rules), `body` on a rule's
 * notify_user and notify_queue. Emitting a bare `{ type, value }` dropped it, so
 * changing a notification's recipient lost its message and the save was a 422. When
 * the verb is unchanged, every key of the loaded entry other than `type` and `value`
 * is carried, ahead of them: `{ ...siblings, type, value }`. A RETYPED row carries
 * nothing: those keys belonged to the old verb.
 */
export function rowsToActions(rows) {
  return (rows ?? []).map((row) => {
    if (row.kind === 'unknown') return row.raw;
    if (pristine(row)) return row.raw;
    return { ...siblingKeys(row), type: row.type, value: row.value };
  });
}

/** The loaded entry's keys other than `type` and `value`, when the verb is unchanged. */
function siblingKeys(row) {
  if (!isPlainObject(row.raw) || derive(row.raw).type !== row.type) return {};
  const { type: _type, value: _value, ...siblings } = row.raw;
  return siblings;
}

/**
 * The builder's own sentences, in the word for what it is building. The same builder
 * edits a macro's actions and an automation rule's, and "This macro only fills in the
 * body" on the rule form is simply wrong. Keyed on the descriptor name.
 */
export function builderWording(descriptorName) {
  const rule = descriptorName === 'automation-rules';
  return {
    empty: rule
      ? 'No actions. This rule changes nothing until one is added.'
      : 'No actions. This macro only fills in the body.',
    cap: (max) => `${max} is the most a ${rule ? 'rule' : 'macro'} can carry.`,
  };
}

/**
 * A fresh row. The verb defaults to the first served one and the value starts
 * BLANK — blank is a row error that blocks Save, which is better than posting a
 * value the server would 422 anyway.
 */
export function newRow(vocabulary, seq) {
  return {
    id: `new-${seq}`, kind: 'known', type: vocabulary?.types?.[0] ?? '', value: '', raw: null,
  };
}

/**
 * Changing a verb CLEARS the value. Carrying `open` across to `set_priority` is a
 * guaranteed 422, and a value silently kept from another vocabulary is the exact
 * class of quiet rewrite this design exists to prevent.
 */
export function retypeRow(row, type) {
  return { ...row, type, value: '' };
}

export function canAddRow(rows) {
  return (rows ?? []).length < MAX_ACTION_ROWS;
}

/**
 * Which control a verb's value takes. The component asks this rather than knowing
 * anything itself, so a verb the server adds tomorrow renders as a text box instead
 * of vanishing.
 *
 *   a served array        a closed dropdown, no HTTP and no loading state
 *   { source: 'users' }   a dropdown from GET /api/users?can_be_assigned=true
 *   { source: 'tags' }    a combobox: unknown tag names are created by ticket
 *                         writes, so a locked list would make a supported workflow
 *                         unauthorable, while a bare text box makes a permanent typo
 *                         (tags refuse deletion and conditions match by name)
 *   anything else         plain text, which the server still validates
 */
export function valueControlFor(type, vocabulary) {
  const spec = vocabulary?.[type];
  if (Array.isArray(spec)) {
    return {
      kind: 'select',
      closed: true,
      options: spec.map((v) => ({ value: String(v), label: humanize(v) })),
    };
  }
  if (spec && typeof spec === 'object' && typeof spec.source === 'string') {
    // A DECLARED text source is the same control as the fallback and a different
    // statement: the descriptor is saying this verb takes prose, rather than the
    // descriptor having forgotten it.
    if (spec.source === 'text') return { kind: 'text', closed: false, source: 'text' };
    return {
      kind: spec.source === 'tags' ? 'combobox' : 'select',
      closed: false,
      source: spec.source,
      options: [],
    };
  }
  return { kind: 'text', closed: false };
}

/**
 * Keep a stored value visible even when it is no longer in its list.
 *
 * A tag can be deleted, and `users.can_be_assigned` is a persisted computed column
 * over `role`, so a role change to collaborator flips it silently and leaves the id
 * sitting in `actions_json`. The console does not get to rewrite a macro because a
 * lookup missed: the option stays, carrying the stored value, labelled so an admin
 * can see why it looks odd. Hiding it is how they never find out.
 */
export function withStoredOption(options, value, control) {
  const list = options ?? [];
  if (value === '' || value === null || value === undefined) return list;
  if (list.some((option) => option.value === value)) return list;
  const label = control?.source === 'users'
    ? `User #${value} (no longer assignable)`
    : `${value} (not in the list)`;
  return [...list, { value, label }];
}

/**
 * Per-row problems, checked here so the builder never posts something the server
 * has already said it will refuse.
 *
 * Deliberately NOT flagged: a stored value merely missing from a FETCHED list.
 * `assign` to a user who is no longer assignable and `add_tag` for a deleted tag
 * both still pass the server's own gate (a digit run, a non-blank name), so blocking
 * Save on them would make a macro uneditable over a lookup that missed.
 *
 * @returns {Array<{index: number, message: string}>}
 */
export function rowProblems(rows, vocabulary) {
  const problems = [];
  (rows ?? []).forEach((row, index) => {
    if (row.kind !== 'known') return;
    const control = valueControlFor(row.type, vocabulary);
    const value = String(row.value ?? '');

    if (value.trim() === '') {
      problems.push({ index, message: `Row ${index + 1}: choose a value for "${humanize(row.type)}".` });
      return;
    }
    if (control.closed && !control.options.some((option) => option.value === value)) {
      problems.push({
        index,
        message: `Row ${index + 1}: "${value}" is not a value ${humanize(row.type)} accepts.`,
      });
    }
  });
  return problems;
}

/**
 * Whether the row builder can be offered at all.
 *
 * False sends the screen to the JSON textarea, which is the floor and stays
 * permanently reachable: a stored non-array is precisely what the column's
 * one-argument ISJSON CHECK still allows, and a macro holding something the builder
 * cannot express must not become both uneditable AND unviewable.
 */
export function builderAvailable(value, vocabulary) {
  return vocabulary !== null && vocabulary !== undefined && Array.isArray(value);
}
